

/*
    When the log originates from the browser, we want to get all the context from the authenticated user,
    and ignore requestId and requestPath.
 */
import { LogLevel } from "#/lib/logging/types";
import { copyError, errorFromDeserializedErrorObject, serializeError } from "#/utils/errors";
import { getUserAction } from "#/next-server/pages/api/user.action";
import { TableRow } from "#/types/supabase";
import { Axiom as AxiomClient, ContentEncoding, ContentType } from "@axiomhq/js";
import { decodeStacktraceAction } from "#next/pages/api/error.action";
import { safeStringify } from "#/utils/general";
import { isBrowser, isBrowserExtension, isNextJSServer } from "#/utils/environment";
import { requestContextStorage } from "#/lib/request-context-wrapper";
import { getUserSession } from "#/browser-extension/src/util/api";


export let axiomClient: AxiomClient = null as any;

axiomClient = new AxiomClient({
  token: process.env.NEXT_PUBLIC_AXIOM_TOKEN,
  orgId: process.env.NEXT_PUBLIC_AXIOM_ORG_ID
});

export type LoggerMetadata = Record<string, any>;

export interface PreparedLoggerArgs {
  message: string,
  metadata?: LoggerMetadata,
  error?: Error
}

export interface PreparedLoggerError {
  message?: string,
  metadata?: LoggerMetadata,
  error: Error
}

export interface LoggerArgs {
  message: string,
  metadata?: LoggerMetadata,
  error?: unknown
}

export interface LoggerError {
  message?: string,
  metadata?: LoggerMetadata,
  error: unknown
}

export type EnvironmentType = "mcp" | "extension" | "webapp";
type InitializeLoggerArgs = {
  environmentType: EnvironmentType,
  headers?: Record<string, string> | null
};

const getEnvironment = () => {
  let environment: typeof process.env.VERCEL_ENV;
  if (isBrowser()) {
    environment = process.env.NEXT_PUBLIC_ENV;
  } else {
    environment = process.env.VERCEL_ENV;
  }
  return environment;
};

const userCache: {
  isInitialized: boolean,
  headers: Record<string, string> | null,
  userPromise: Promise<TableRow<"users"> | null>,
  environmentType: EnvironmentType
} = {
  isInitialized: false,
  headers: null,
  userPromise: Promise.resolve(null),
  environmentType: null as any
};

interface LoggedMessageStructure {
  level: LogLevel,
  environment: string,
  message: string,
  user?: TableRow<"users">,
  environmentType: EnvironmentType,
  error?: {
    name: string,
    message: string,
    stack?: string
  },
  metadata?: string
}

const enrichLogContext = (
  level: LogLevel,
  args: PreparedLoggerArgs | PreparedLoggerError,
  user: TableRow<"users"> | null,
  environmentType: EnvironmentType
): LoggedMessageStructure => {
  const enrichedArgs = { ...args };

  if (enrichedArgs.error && !enrichedArgs.message) {
    enrichedArgs.message = enrichedArgs.error.message;
  }

  const message = enrichedArgs.message || "No message provided";

  return {
    level,
    environment: getEnvironment(),
    message,
    user: user ?? undefined,
    environmentType,
    ...(enrichedArgs.metadata ? { metadata: safeStringify(enrichedArgs.metadata, 2) } : {}),
    ...(enrichedArgs.error ? { error: enrichedArgs.error } : {})
  };
};

const logCriticalLoggingFailure = async (
  message: string,
  args: PreparedLoggerArgs | PreparedLoggerError,
  environmentType: EnvironmentType,
  errorWhileLogging?: { error: unknown, parentStack: string | undefined }
): Promise<void> => {
  const error = errorWhileLogging?.error instanceof Error
    ? errorWhileLogging.error
    : errorWhileLogging?.error
      ? errorFromDeserializedErrorObject(errorWhileLogging.error)
      : new Error("Error while logging");

  if (errorWhileLogging && error.stack) {
    error.stack = error.stack.includes("(Outer Stack)")
      ? error.stack
      : `${error.stack}\n\n(Outer Stack):${errorWhileLogging.parentStack}`;
  }

  const { metadata, ...argsWithoutMetadata } = args;
  const toLog: Omit<LoggedMessageStructure, "user" | "level"> & { level: "critical", user: TableRow<"users"> | null } = {
    level: "critical",
    environment: getEnvironment(),
    user: await userCache.userPromise ?? null,
    environmentType,
    message,
    ...argsWithoutMetadata,
    ...(metadata ? { metadata: safeStringify(metadata, 2) } : {}),
    ...(errorWhileLogging && { errorWhileLogging: JSON.parse(serializeError(error)) })
  };

  const environment = getEnvironment();
  if (environment !== "production") {
    console.error(safeStringify(toLog, 2));
    if (environment === "development" && process.env.LOG_TO_AXIOS_IN_DEV !== "true") {
      return;
    }
  }

  try {
    await axiomClient.ingestRaw(
      "logging",
      safeStringify([ toLog ]),
      ContentType.JSON,
      ContentEncoding.Identity
    );
  } catch (e) {
    if (e instanceof Error && e.message.includes("Forbidden")) {
      return;
    }
    console.error("Critical logging failed", { originalError: e, payload: toLog });
    try {
      await axiomClient.ingestRaw(
        "logging",
        safeStringify([ {
          environment,
          error: String(e),
          stack: e instanceof Error ? e.stack : undefined
        } ]),
        ContentType.JSON,
        ContentEncoding.Identity
      );
    } catch {
      console.error("Critical logging backup failed", { originalError: e, payload: toLog });
    }
  }
};

const logMessage = (level: LogLevel) => (
  argsInput: LoggerArgs | LoggerError
): void => {
  if (
    !userCache.isInitialized
    && !isNextJSServer()
    && !isBrowserExtension()
  ) {
    throw new Error("Logger not initialized when it should be!");
  }

  let environmentType: EnvironmentType;
  if (isBrowserExtension()) {
    environmentType = "extension";
  } else if (isNextJSServer()) {
    environmentType = "webapp";
  } else {
    environmentType = userCache.environmentType;
  }

  const _args = prepareLoggerArgs(argsInput);
  const args: PreparedLoggerArgs | PreparedLoggerError = {
    ..._args,
    ...(_args?.error ? { error: errorFromDeserializedErrorObject(_args.error) } : {})
  };
  (async () => {
    let user: TableRow<"users"> | null = null;
    if (environmentType === "webapp") {
      if (!isBrowser()) {
        const store = requestContextStorage?.getStore();
        if (store) {
          user = await getUserAction({
            userId: store.userId,
            headers: {
              "x-secret": process.env.SUPABASE_WEBHOOK_SECRET
            }
          }).catch(err => {
            console.error("Error while fetching user", err);
            throw err;
          });
        }
      } else {
        try {
          user = await getUserAction();
        } catch {}
      }
    } else if (environmentType === "extension") {
      const session = await getUserSession();
      userCache.headers = await session.getSessionHeaders();
      if (userCache.headers) {
        const userPromise = getUserAction({
          userId: session.user.id,
          headers: userCache.headers
        }).catch(err => {
          console.error("Error while fetching user", err);
          throw err;
        });
        userCache.userPromise = userPromise;
        user = await userPromise;
      }

    } else {
      user = await userCache.userPromise ?? null;
    }

    if (args?.error?.stack) {
      const { newStackTrace } = await decodeStacktraceAction({
        stacktrace: args.error.stack
      });
      args.error.stack = newStackTrace;
    }
    const environment = getEnvironment();
    const toLog = enrichLogContext(level, args, user, environmentType);
    if (environment !== "production") {
      console[level](safeStringify(toLog, 2));
      if (environment === "development" && process.env.LOG_TO_AXIOS_IN_DEV !== "true") {
        return;
      }
    }
    await axiomClient.ingestRaw(
      process.env.NEXT_PUBLIC_AXIOM_DATASET,
      safeStringify([ toLog ]),
      ContentType.JSON,
      ContentEncoding.Identity
    );
  })()
    .catch(async e => {
      await logCriticalLoggingFailure("Error while logging", args, environmentType, { error: e, parentStack: e.stack });
    });
};

function prepareLoggerArgs(
  args: LoggerArgs | LoggerError
): PreparedLoggerArgs | PreparedLoggerError {
  if (args.error && !(args.error instanceof Error)) {
    args.error = errorFromDeserializedErrorObject(args.error);
  }
  return args as PreparedLoggerArgs | PreparedLoggerError;
}

export interface TLogger {
  info(args: LoggerArgs): void,
  debug(args: LoggerArgs): void,
  warn(args: LoggerArgs): void,
  error(args: LoggerError): Error,
  initialize(args: InitializeLoggerArgs): void,
  get isInitialized(): boolean
}

export const Logger: TLogger = {
  info: logMessage("info"),
  debug: logMessage("debug"),
  warn: logMessage("warn"),
  error: (args: LoggerError) => {
    const error = errorFromDeserializedErrorObject(args.error);
    const errorCopy = copyError(error);
    if (errorCopy?.message) {
      Object.defineProperty(errorCopy, "message", {
        value: errorCopy.message,
        writable: true
      });
    }
    errorCopy.message = args.message && args.message !== error.message
      ? `${args.message}\n${error.message}`
      : error.message;
    try {
      logMessage("error")(prepareLoggerArgs(args));
    } catch {}
    return errorCopy;
  },
  initialize: args => {
    if (!process.env.NEXT_PUBLIC_AXIOM_TOKEN || !process.env.NEXT_PUBLIC_AXIOM_ORG_ID) {
      throw new Error("Axiom token and orgId must be provided");
    }

    axiomClient = new AxiomClient({
      token: process.env.NEXT_PUBLIC_AXIOM_TOKEN,
      orgId: process.env.NEXT_PUBLIC_AXIOM_ORG_ID
    });

    // if (!args.jwtToken && !args.userId) {
    //   throw new Error("Either jwtToken or userId must be provided to initialize the logger");
    // }
    if (args.headers) {
      userCache.userPromise = getUserAction({
        headers: args.headers
      }).catch(err => {
        console.error("Error while fetching user", err);
        throw err;
      });
      userCache.isInitialized = true;
    }
    userCache.environmentType = args.environmentType;
    userCache.headers = args.headers ?? null;
  },
  get isInitialized() {
    return userCache.isInitialized;
  }
};